import { forwardRef } from 'react';
import { Box } from '@mui/material';
import type { Employee, SalarySlip } from '../../types';
import { formatINR } from '../../utils/formatINR';
import { numberToWordsWithOnly } from '../../utils/numberToWords';

interface PayslipDocumentProps {
  slip: SalarySlip;
  /** Falls back to slip.employeeSnapshot fields if employee is unavailable. */
  employee?: Employee | null;
  companyName?: string;
}

const INK = '#0A0A0A';
const MUTED = '#737373';
const HAIRLINE = '#E5E5E5';
const SUBTLE_BG = '#FAFAFA';

const monthName = (month: string): string => {
  // month format: "YYYY-MM"
  const [yearStr, monthStr] = month.split('-');
  const m = Math.max(1, Math.min(12, parseInt(monthStr || '0', 10))) - 1;
  const names = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[m]} ${yearStr || ''}`.trim();
};

const formatDOB = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Kotak Securities-style A4 payslip — used in:
 *  - Payroll Review step (admin verifies before sending)
 *  - MyPayslips employee view
 *  - PDF export (via react-to-print)
 *
 * Width capped at 794px (210mm @ 96dpi). Print CSS in this file sizes it to
 * A4 with 12mm margins for paper output.
 */
const PayslipDocument = forwardRef<HTMLDivElement, PayslipDocumentProps>(
  function PayslipDocument({ slip, employee, companyName = 'Percent Investments' }, ref) {
    // Identity fields reflect the CURRENT employee record, so corrections to
    // joining date, designation, PAN, bank, etc. show on every slip. Fall back
    // to the frozen snapshot when the live record isn't available (e.g. the
    // employee viewing their own slip). Financial amounts stay frozen on the slip.
    const snap = slip.employeeSnapshot;
    const code = employee?.employeeCode ?? snap?.employeeCode ?? '—';
    const grade = employee?.grade ?? snap?.grade ?? '—';
    const uan = employee?.uanNumber ?? snap?.uanNumber ?? '—';
    const esic = employee?.esicNumber ?? snap?.esicNumber ?? '—';
    const dob = employee?.dateOfBirth ?? snap?.dateOfBirth;
    const pan = employee?.panNumber ?? snap?.panNumber ?? '—';
    const pfNo = employee?.pfNumber ?? snap?.pfNumber ?? '—';
    const joiningDate = employee?.joiningDate ?? snap?.joiningDate;
    const bankName = employee?.bankName ?? slip.bankName ?? snap?.bankName ?? '—';
    const bankAccountNo = employee?.bankAccountNo ?? slip.bankAccountNo ?? snap?.bankAccountNo ?? '—';
    const designation = employee?.designation ?? slip.designation ?? '—';

    const earnings = slip.earnings;
    const monthlyEarnings = employee?.salaryComponents;
    const deductions = slip.deductions;
    const grossEarnings = slip.grossEarnings;
    const totalDeductions = slip.totalDeductions;
    const netPay = slip.netPay;

    // Earnings rows: only rows the user has > 0 on the slip OR > 0 monthly rate.
    // BONUS is always shown (template includes it). DA is shown only when > 0
    // (legacy fallback for employees whose components still carry a non-zero DA).
    const earningsRows: { label: string; monthly: number; current: number }[] = [
      { label: 'Basic Salary', monthly: monthlyEarnings?.basic ?? earnings.basic, current: earnings.basic },
      { label: 'House Rent Allowance', monthly: monthlyEarnings?.hra ?? earnings.hra, current: earnings.hra },
      { label: 'Special Allowance', monthly: monthlyEarnings?.specialAllowance ?? earnings.specialAllowance, current: earnings.specialAllowance },
      { label: 'BONUS', monthly: (monthlyEarnings?.bonus ?? earnings.bonus ?? 0), current: earnings.bonus ?? 0 },
    ];
    if ((earnings.da ?? 0) > 0 || (monthlyEarnings?.da ?? 0) > 0) {
      earningsRows.splice(3, 0, {
        label: 'Dearness Allowance',
        monthly: monthlyEarnings?.da ?? earnings.da,
        current: earnings.da,
      });
    }
    if ((earnings.otherAllowances ?? 0) > 0) {
      earningsRows.push({
        label: 'Other Allowance',
        monthly: monthlyEarnings?.otherAllowances ?? earnings.otherAllowances,
        current: earnings.otherAllowances,
      });
    }

    // Deduction rows: only render rows with a non-zero amount.
    // If everything is zero, keep "Professional Tax ₹0" so the table isn't empty.
    type DedRow = { label: string; amount: number };
    const allDeductionRows: DedRow[] = [
      { label: 'Professional Tax', amount: deductions.professionalTax },
      { label: 'Income Tax', amount: deductions.incomeTax ?? deductions.tds ?? 0 },
      { label: 'Provident Fund', amount: deductions.pf },
      { label: 'Payroll Giving', amount: deductions.payrollGiving ?? 0 },
      { label: 'Mediclaim Premium Recovery', amount: deductions.mediclaim ?? 0 },
    ];
    if ((deductions.otherDeductions ?? 0) > 0) {
      allDeductionRows.push({ label: 'Other Deductions', amount: deductions.otherDeductions });
    }
    const visibleDeductionRows = allDeductionRows.filter((r) => r.amount > 0);
    const deductionRows = visibleDeductionRows.length > 0
      ? visibleDeductionRows
      : [{ label: 'Professional Tax', amount: 0 }];

    return (
      <Box
        ref={ref}
        className="payslip-document"
        sx={{
          width: '100%',
          maxWidth: 794,
          mx: 'auto',
          bgcolor: '#FFFFFF',
          color: INK,
          fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
          fontSize: 11,
          lineHeight: 1.45,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 1,
          overflow: 'hidden',
          '@media print': {
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            maxWidth: 'none',
            width: '100%',
          },
        }}
      >
        {/* Header — pre-cropped Percent Investments wordmark (844×222 asset,
            generated from the original 1080×1080 logo) + period line. No
            overflow tricks needed; the asset itself is tightly cropped, so it
            prints identically via react-to-print. */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${HAIRLINE}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src="/percent_logo_cropped.png"
              alt={companyName}
              sx={{ width: 190, height: 50, display: 'block' }}
            />
          </Box>
          <Box
            sx={{
              mt: 2,
              fontSize: 13,
              fontWeight: 600,
              textAlign: 'center',
              color: INK,
            }}
          >
            Payslip for the month of {monthName(slip.month)}
          </Box>
        </Box>

        {/* Employee details — two-column key-value grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            borderBottom: `1px solid ${HAIRLINE}`,
          }}
        >
          {[
            // Left column
            ['Designation', designation],
            ['Employee Code', code],
            ['Bank Name', bankName],
            ['Permanent Account Number', pan],
            ['Date of Joining', joiningDate ? formatDOB(joiningDate) : '—'],
            ['Days Worked', String(slip.effectiveWorkingDays)],
            ['ESIC Account Number', esic],
            ['UAN Number', uan],
          ].map(([k, v], idx) => (
            <Box
              key={`L-${idx}`}
              sx={{
                px: 2.5,
                py: 1,
                borderRight: `1px solid ${HAIRLINE}`,
                borderBottom: idx < 7 ? `1px solid ${HAIRLINE}` : 'none',
                display: 'flex',
                gap: 1,
              }}
            >
              <Box sx={{ color: MUTED, minWidth: 140 }}>{k}</Box>
              <Box sx={{ color: INK, fontWeight: 500, wordBreak: 'break-word' }}>{v}</Box>
            </Box>
          ))}
          {[
            // Right column (matches Kotak — 7 items, no filler)
            ['Grade', grade],
            ['Employee Name', slip.employeeName],
            ['Bank Account Number', bankAccountNo],
            ['PF Account Number', pfNo],
            ['Date of Birth', dob ? formatDOB(dob) : '—'],
            ['LWP', String(slip.lwp ?? 0)],
            ['Arrears Days', String(slip.arrearsDays ?? 0)],
          ].map(([k, v], idx, arr) => (
            <Box
              key={`R-${idx}`}
              sx={{
                px: 2.5,
                py: 1,
                borderBottom: idx < arr.length - 1 ? `1px solid ${HAIRLINE}` : 'none',
                display: 'flex',
                gap: 1,
              }}
            >
              <Box sx={{ color: MUTED, minWidth: 140 }}>{k}</Box>
              <Box sx={{ color: INK, fontWeight: 500, wordBreak: 'break-word' }}>{v}</Box>
            </Box>
          ))}
        </Box>

        {/* Earnings + Deductions side-by-side */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr' }}>
          {/* Earnings — single Current Month amount column (mirrors Deductions) */}
          <Box sx={{ borderRight: `1px solid ${HAIRLINE}` }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1.1fr',
                bgcolor: SUBTLE_BG,
                borderBottom: `1px solid ${HAIRLINE}`,
                fontWeight: 600,
                color: INK,
                fontSize: 11,
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>Earnings</Box>
              <Box sx={{ px: 1.5, py: 1, textAlign: 'right' }}>Current Month</Box>
            </Box>
            {earningsRows.map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.6fr 1.1fr',
                  borderBottom: `1px solid ${HAIRLINE}`,
                }}
              >
                <Box sx={{ px: 2, py: 0.9 }}>{row.label}</Box>
                <Box sx={{ px: 1.5, py: 0.9, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatINR(row.current)}
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1.1fr',
                bgcolor: SUBTLE_BG,
                fontWeight: 600,
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>Gross Earnings</Box>
              <Box sx={{ px: 1.5, py: 1, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatINR(grossEarnings)}
              </Box>
            </Box>
          </Box>

          {/* Deductions */}
          <Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1.1fr',
                bgcolor: SUBTLE_BG,
                borderBottom: `1px solid ${HAIRLINE}`,
                fontWeight: 600,
                color: INK,
                fontSize: 11,
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>Deductions</Box>
              <Box sx={{ px: 1.5, py: 1, textAlign: 'right' }}>Amount</Box>
            </Box>
            {deductionRows.map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.6fr 1.1fr',
                  borderBottom: `1px solid ${HAIRLINE}`,
                }}
              >
                <Box sx={{ px: 2, py: 0.9 }}>{row.label}</Box>
                <Box sx={{ px: 1.5, py: 0.9, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatINR(row.amount)}
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1.1fr',
                bgcolor: SUBTLE_BG,
                fontWeight: 600,
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>Total Deductions</Box>
              <Box sx={{ px: 1.5, py: 1, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatINR(totalDeductions)}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer — in-words + Net Salary */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            borderTop: `1px solid ${HAIRLINE}`,
            px: 2.5,
            py: 1.5,
          }}
        >
          <Box sx={{ color: MUTED, fontSize: 11 }}>
            In words : {numberToWordsWithOnly(netPay)} ₹
          </Box>
          <Box sx={{ fontSize: 13, fontWeight: 700, color: INK }}>
            Net Salary : {formatINR(netPay)}
          </Box>
        </Box>

      </Box>
    );
  }
);

export default PayslipDocument;
